import { Fragment } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import type { TSelector } from "../../../i18n/types";
import { FIXED_AMOUNT_ACTIONS, OPERATOR_OPTIONS } from "./constants";
import { getActionLabel, getMetricLabel, getMetricNumber } from "./labels";

const RELATIVE_METRIC_LABEL_SELECTORS: Record<string, TSelector> = {
  target_acos: ($) => $.programs.relativeMetric["Optimal ACOS Range Low"],
  max_acos: ($) => $.programs.relativeMetric["Optimal ACOS Range High"],
  target_roas: ($) => $.programs.relativeMetric["Optimal ROAS Range High"],
  min_roas: ($) => $.programs.relativeMetric["Optimal ROAS Range Low"],
  bid: ($) => $.programs.relativeMetric["Current Bid"],
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = TFunction<any, any>;

const sanitize = (value: string): string => value.replaceAll(/(^[^a-zA-Z0-9]+)|([^a-zA-Z0-9]+$)/g, "");

const MATCH_TYPE_ACCESSORS = new Set(["target_match_types", "keyword_match_types"]);

const TAG_ACCESSORS = new Set(["tags_enhanced"]);

type ConditionScope = { values?: { days?: string | number } } | null | undefined;

interface RuleCondition {
  option: string;
  values?: unknown[];
  scope?: ConditionScope;
}

interface RuleAction {
  function: string;
  values?: unknown[];
  operator?: string;
}

interface TagOption {
  value: unknown;
  label: string;
}

interface RuleSummaryProps {
  conditions: RuleCondition[];
  logicalOperator: string;
  actions: RuleAction[];
  marketPlace: string;
  tagOptions?: TagOption[];
  enhancedTagOptions?: TagOption[];
  engine: string;
}

const Strong = <strong />;

const toFiniteNumber = (val: unknown): number => {
  if (typeof val === "number") return val;
  if (typeof val === "string") return parseFloat(val);
  return NaN;
};

const formatActionAmount = (value: unknown, operator: string | undefined, marketPlace: string): string => {
  const numeric = toFiniteNumber(value);
  if (!Number.isFinite(numeric)) return "";
  if (operator === "adjust_fixed") return formatCurrency(numeric, { marketPlace });
  return formatPercent(numeric / 100, { decimalPlaces: 0 });
};

function ConditionFragment({ condition, engine, t }: { condition: RuleCondition; engine: string; t: TFn }) {
  const [operatorKey, metricRaw] = JSON.parse(condition.option) as [string, string];
  const metric = sanitize(metricRaw);

  let conditionDays: number | undefined;
  if (engine !== "tagging" && condition.scope?.values?.days) {
    const parsed = parseInt(String(condition.scope.values.days), 10);
    if (Number.isFinite(parsed)) conditionDays = parsed;
  }

  const operatorOption = OPERATOR_OPTIONS["number"].find((o) => o.value === operatorKey);
  const operatorLabel =
    operatorOption && typeof operatorOption.label === "function" ? t(operatorOption.label) : operatorKey;

  const metricLabel = getMetricLabel(t, metric);
  const number = getMetricNumber(metric);

  const rawValues = Array.isArray(condition.values) ? condition.values : [];

  const formatValue = (v: unknown): string => {
    if (typeof v === "string") {
      if (v.startsWith("${") && v.endsWith("}")) {
        const inner = sanitize(v);
        const relativeSelector = RELATIVE_METRIC_LABEL_SELECTORS[inner];
        if (relativeSelector) return t(relativeSelector);
        return getMetricLabel(t, inner);
      }
      return v;
    }
    if (typeof v === "number") return String(v);
    return "";
  };

  const value = formatValue(rawValues[0]);
  const value1 = formatValue(rawValues[0]);
  const value2 = formatValue(rawValues[1]);

  return (
    <Trans
      t={t}
      i18nKey={($) => $.programs.summary.condition}
      values={{
        metric: metricLabel,
        hasScope: conditionDays != null ? "yes" : "no",
        days: conditionDays ?? 0,
        number,
        op: operatorKey === "between" ? "between" : "other",
        operatorLabel,
        value,
        value1,
        value2,
      }}
      components={{ m: Strong, n: Strong, v: Strong, o: Strong }}
    />
  );
}

function ConditionList({
  conditions,
  logicalOperator,
  engine,
  t,
}: {
  conditions: RuleCondition[];
  logicalOperator: string;
  engine: string;
  t: TFn;
}) {
  const separator = logicalOperator === "any" ? t(($) => $.programs.summary.or) : t(($) => $.programs.summary.and);
  const comma = t(($) => $.programs.summary[", "]);

  return (
    <>
      {conditions.map((condition, i) => (
        <Fragment key={i}>
          <ConditionFragment condition={condition} engine={engine} t={t} />
          {i < conditions.length - 2 && <span>{comma}</span>}
          {i === conditions.length - 2 && <span>{` ${separator} `}</span>}
        </Fragment>
      ))}
    </>
  );
}

function MatchTypeConditionList({ conditions, t }: { conditions: RuleCondition[]; t: TFn }) {
  const or = t(($) => $.programs.summary.or);

  return (
    <>
      {conditions.map((condition, i) => {
        const [, metricRaw] = JSON.parse(condition.option) as [string, string];
        const metric = sanitize(metricRaw);
        const rawValues = Array.isArray(condition.values) ? condition.values.flat() : [];
        const matchTypes = rawValues.filter((v): v is string => typeof v === "string" && v.length > 0);

        if (matchTypes.length === 0) return null;

        return (
          <Fragment key={i}>
            {" "}
            <Trans
              t={t}
              i18nKey={($) => $.programs.summary.match_type_clause}
              values={{ metric: getMetricLabel(t, metric), matchTypes: matchTypes.join(", ") }}
              components={{ m: Strong, v: Strong }}
            />
            {i < conditions.length - 1 && <span>{` ${or} `}</span>}
          </Fragment>
        );
      })}
    </>
  );
}

function TagConditionList({
  conditions,
  enhancedTagOptions,
  t,
}: {
  conditions: RuleCondition[];
  enhancedTagOptions: TagOption[];
  t: TFn;
}) {
  const or = t(($) => $.programs.summary.or);

  return (
    <>
      {conditions.map((condition, i) => {
        const rawValues = Array.isArray(condition.values) ? condition.values.flat() : [];
        const tagNames = rawValues
          .map((v) => {
            const match = enhancedTagOptions.find((option) => option.value === v);
            if (match) return match.label;
            return typeof v === "string" || typeof v === "number" ? String(v) : "";
          })
          .filter((name): name is string => name.length > 0);

        if (tagNames.length === 0) return null;

        return (
          <Fragment key={i}>
            {" "}
            <Trans
              t={t}
              i18nKey={($) => $.programs.summary.tag_clause}
              values={{ tags: tagNames.join(", ") }}
              components={{ v: Strong }}
            />
            {i < conditions.length - 1 && <span>{` ${or} `}</span>}
          </Fragment>
        );
      })}
    </>
  );
}

function ActionFragment({
  action,
  marketPlace,
  tagOptions,
  t,
}: {
  action: RuleAction;
  marketPlace: string;
  tagOptions: TagOption[];
  t: TFn;
}) {
  const actionKey = action.function;
  const rawValues = Array.isArray(action.values) ? action.values : [];

  if (actionKey === "ADD_TAG") {
    const tagId = rawValues[0];
    const tagIdString = typeof tagId === "string" || typeof tagId === "number" ? String(tagId) : "";
    const tagLabel = tagOptions.find((option) => option.value === tagId)?.label ?? tagIdString;
    return (
      <Trans
        t={t}
        i18nKey={($) => $.programs.summary.action_add_tag}
        values={{ tag: tagLabel }}
        components={{ s: Strong }}
      />
    );
  }

  if (actionKey === "SET_BID_CPC") {
    const rawAmount = rawValues[0];
    const numeric = toFiniteNumber(rawAmount);
    let sign: "plus" | "minus" | "none" = "none";
    let amount = "";
    if (Number.isFinite(numeric) && numeric !== 0) {
      sign = numeric > 0 ? "plus" : "minus";
      const abs = Math.abs(numeric);
      amount =
        action.operator === "adjust_fixed" ?
          formatCurrency(abs, { marketPlace })
        : formatPercent(abs / 100, { decimalPlaces: 0 });
    }

    const rawDays = toFiniteNumber(rawValues[1]);
    const days = Number.isFinite(rawDays) ? Math.trunc(rawDays) : 0;

    return (
      <Trans
        t={t}
        i18nKey={($) => $.programs.summary.action_set_bid_cpc}
        values={{ days, sign, amount }}
        components={{ s: Strong, v: Strong }}
      />
    );
  }

  const verb = getActionLabel(t, actionKey);
  const noValueActions = new Set(["USE_ALGO", "DO_NOTHING", "PAUSE"]);
  let changeKind: "by" | "to" | "none" = "by";
  if (actionKey.toLowerCase().includes("set")) changeKind = "to";
  if (noValueActions.has(actionKey)) changeKind = "none";

  // Rules saved before these lost their percent option can still carry adjust_percent; the engine ignores it.
  const operator = FIXED_AMOUNT_ACTIONS.includes(actionKey) ? "adjust_fixed" : action.operator;
  const amount = changeKind === "none" ? "" : formatActionAmount(rawValues[0], operator, marketPlace);

  return (
    <Trans
      t={t}
      i18nKey={($) => $.programs.summary.action_default}
      values={{ verb, changeKind, amount }}
      components={{ s: Strong, v: Strong }}
    />
  );
}

function ActionsList({
  actions,
  marketPlace,
  tagOptions,
  t,
}: {
  actions: RuleAction[];
  marketPlace: string;
  tagOptions: TagOption[];
  t: TFn;
}) {
  const and = t(($) => $.programs.summary.and);
  const comma = t(($) => $.programs.summary[", "]);

  return (
    <>
      {actions.map((action, i) => (
        <Fragment key={i}>
          <ActionFragment action={action} marketPlace={marketPlace} tagOptions={tagOptions} t={t} />
          {i < actions.length - 2 && <span>{comma}</span>}
          {i === actions.length - 2 && <span>{` ${and} `}</span>}
        </Fragment>
      ))}
    </>
  );
}

export function RuleSummary({
  conditions,
  logicalOperator,
  actions,
  marketPlace,
  tagOptions = [],
  enhancedTagOptions = [],
  engine,
}: RuleSummaryProps) {
  const { t } = useTranslation(["programs"]);

  const nonMatchTypeConditions: RuleCondition[] = [];
  const matchTypeConditions: RuleCondition[] = [];
  const tagConditions: RuleCondition[] = [];
  for (const condition of conditions) {
    if (!condition.option) continue;
    try {
      const [, metricRaw] = JSON.parse(condition.option) as [string, string];
      const metric = sanitize(metricRaw);
      if (MATCH_TYPE_ACCESSORS.has(metric)) {
        matchTypeConditions.push(condition);
      } else if (TAG_ACCESSORS.has(metric)) {
        tagConditions.push(condition);
      } else {
        nonMatchTypeConditions.push(condition);
      }
    } catch {
      // Skip unparseable conditions.
    }
  }

  const taggingDays =
    engine === "tagging" && conditions[0]?.scope?.values?.days != null ?
      parseInt(String(conditions[0].scope.values.days), 10)
    : undefined;

  const conditionList = (
    <ConditionList conditions={nonMatchTypeConditions} logicalOperator={logicalOperator} engine={engine} t={t} />
  );
  const matchTypeList = <MatchTypeConditionList conditions={matchTypeConditions} t={t} />;
  const tagList = <TagConditionList conditions={tagConditions} enhancedTagOptions={enhancedTagOptions} t={t} />;
  const actionsList = <ActionsList actions={actions} marketPlace={marketPlace} tagOptions={tagOptions} t={t} />;

  const conditionsAndMatch = (
    <>
      {conditionList}
      {matchTypeList}
      {tagList}
    </>
  );

  if (conditions.length === 0) {
    return (
      <Trans t={t} i18nKey={($) => $.programs.summary["Always {actions}."]} components={{ actions: actionsList }} />
    );
  }

  if (engine === "tagging" && taggingDays != null && Number.isFinite(taggingDays)) {
    return (
      <Trans
        t={t}
        i18nKey={($) => $.programs.summary["When over {days} days, {conditions}, {actions}."]}
        values={{ days: taggingDays }}
        components={{ n: Strong, conditions: conditionsAndMatch, actions: actionsList }}
      />
    );
  }

  return (
    <Trans
      t={t}
      i18nKey={($) => $.programs.summary["When {conditions}, {actions}."]}
      components={{ conditions: conditionsAndMatch, actions: actionsList }}
    />
  );
}
